import React, { useState } from 'react';
import { InView } from 'react-intersection-observer';

import CustomSpinner, { CustomSpinnerColor } from '@components/Spinner/CustomSpinner';
import useEventTracking from '@hooks/useEventTracking';
import useOrganizationAnalytics from '@hooks/useOrganizationAnalytics';
import { ImpressionElement, impression } from '@tracking';
import { TrackingLocation } from '@tracking';
import { CORPORATE_EMAIL_ADDRESS } from '@utils/constants';

import SectionLayout from '../SectionLayout';
import { PERIOD_OPTIONS, PeriodOption } from '../types';
import ActiveSubscribersChart from './ActiveSubscribersChart';
import MostReadArticles from './MostReadArticles';
import ReadershipMetrics from './ReadershipMetrics';
import SubscriberMetrics from './SubscriberMetrics';

interface AnalyticsSectionProps {
  canViewAnalytics: boolean;
}

const AnalyticsSection = ({ canViewAnalytics }: AnalyticsSectionProps) => {
  const { Track, trackEvent } = useEventTracking({
    location: TrackingLocation.Analytics,
  });
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>(PERIOD_OPTIONS[0]);
  const { data, isLoading, isError } = useOrganizationAnalytics(canViewAnalytics);

  return (
    <Track>
      <SectionLayout
        id="analytics"
        title="Analytics"
        note="An overview of your account’s stats and data."
      >
        {!canViewAnalytics ? (
          <NoAccessState />
        ) : isLoading ? (
          <CustomSpinner
            size={15}
            color={CustomSpinnerColor.PrimaryMagenta}
            style={{ display: 'flex', justifyContent: 'center', marginTop: '56px' }}
          />
        ) : isError ? (
          <ErrorState />
        ) : (
          <InView
            triggerOnce={true}
            onChange={(inView) => {
              inView && trackEvent(impression({ impressionElement: ImpressionElement.Analytics }));
            }}
          >
            <SubscriberMetrics
              activeSubscribers={data.activeSubscribers}
              proUpgradeUserRequests={data.proUpgradeUserRequests}
            />
            <ActiveSubscribersChart
              labels={data.activeSubscriberGrowthByMonth.map((item) => item.month)}
              data={data.activeSubscriberGrowthByMonth.map((item) => item.activeSubscribers)}
            />
            <ReadershipMetrics
              reads={data.reads}
              selectedPeriod={selectedPeriod}
              setSelectedPeriod={setSelectedPeriod}
            />
            <MostReadArticles
              articles={data.topArticles[selectedPeriod.value].articles}
              selectedPeriod={selectedPeriod}
            />
          </InView>
        )}
      </SectionLayout>
    </Track>
  );
};

const ErrorState = () => (
  <p className="my-20 font-suisse-intl text-button-sm">
    Something went wrong while loading analytics. If the issue persists, please contact{' '}
    <a href="mailto:corporate@theinformation.com" className="underline hover:text-primary-magenta">
      {CORPORATE_EMAIL_ADDRESS}
    </a>
    .
  </p>
);

const NoAccessState = () => (
  <p className="my-20 font-suisse-intl text-button-sm">
    You don&rsquo;t have access to analytics, please contact{' '}
    <a href="mailto:corporate@theinformation.com" className="underline hover:text-primary-magenta">
      {CORPORATE_EMAIL_ADDRESS}
    </a>
    .
  </p>
);

export default AnalyticsSection;
